import type { QueryList } from '@angular/core';
import { ChangeDetectorRef, Component, Input, output, ViewChildren } from '@angular/core';
import type { AbstractControl, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';

import { merge as observableMerge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { TranslatePipe } from '@recruitee/i18n';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DividerComponent,
  GroupComponent,
  HotkeysService,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  KeyCode,
  PhoneInputComponent,
} from '@recruitee/user-interface';

import type { InformationField } from '../candidate-contact-card/candidate-profile-information.enums';

@Component({
  selector: 'rt-profile-information-edit',
  templateUrl: './profile-information-edit.component.html',
  styleUrls: ['./profile-information-edit.component.less'],
  imports: [
    ReactiveFormsModule,
    GroupComponent,
    PhoneInputComponent,
    InputComponent,
    IconButtonComponent,
    ButtonComponent,
    IconComponent,
    DividerComponent,
    ButtonLoadingDirective,
    TranslatePipe,
  ],
})
export class ProfileInformationEditComponent {
  @Input() public group: UntypedFormGroup;
  @Input() public arrayName: InformationField;
  @Input() public arrayControls: UntypedFormArray;
  @Input() public loading: boolean;
  @Input() public errorText: string = '';
  @Input() public editPhoneNumber: boolean = false;
  @Input() public countryCode: string | null;
  @Input() public hasNewStyling: boolean = false;
  public add = output<InformationField>();
  public save = output<InformationField>();
  public cancel = output<void>();
  public removeItem = output<{
    arrayName: InformationField;
    index: number;
  }>();

  @ViewChildren('inputField') private inputComps: QueryList<InputComponent | PhoneInputComponent>;
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private hotkey: HotkeysService,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.hotkey
      .shortcut([KeyCode.Enter], false, false)
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => this.onAdd());

    observableMerge(this.arrayControls.valueChanges, this.arrayControls.statusChanges)
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => this.cdRef.detectChanges());
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public trackByValue(index: number, abstractControl: AbstractControl): AbstractControl {
    return abstractControl;
  }

  public onAdd(): void {
    this.add.emit(this.arrayName);
    this.inputComps.last.setFocus();
  }

  public onCancel(): void {
    this.cancel.emit();
  }

  public onSave(): void {
    this.save.emit(this.arrayName);
  }

  public onRemove(index: number): void {
    this.removeItem.emit({ arrayName: this.arrayName, index });
  }

  public onBlur(control: AbstractControl): void {
    control.setValue(control.value);
    control.markAsTouched();
    control.updateValueAndValidity();
    this.cdRef.detectChanges();
  }
}
